import { Button as AIMEButton } from "tot-loft-component-library";

import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { resetReferrals, setSearchQuery } from "./redux/slices/ReferralsSlice";
import { fetchReferralsItems } from "./redux/slices/ReferralsThunks";

export default function Adminreferralsone() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const referrals = useSelector((state) => state.referrals.items);
  const referralsLoading = useSelector((state) => state.referrals.loading);
  const hasMoreReferrals = useSelector((state) => state.referrals.hasMore);
  const currentPage = useSelector((state) => state.referrals.currentPage);

  // Local state
  const [processedData, setProcessedData] = useState([]);
  const [tableColumn, setTableColumn] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const searchTimeoutRef = useRef(null);

  // Refs for infinite scroll
  const observerRef = useRef();
  const sentinelRef = useRef();
  const isInitialLoad = useRef(true);

  // // Debounce search input
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setSearchDebounced(search);
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [search]);

  const formattedDate = (date) => {
    const newDate = new Date(date);
    const formatted = newDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return formatted;
  };

  // Load more data function
  const loadMoreData = useCallback(() => {
    if (referralsLoading || !hasMoreReferrals) return;

    dispatch(
      fetchReferralsItems({
        page: currentPage + 1,
        limit: 20,
        search: searchDebounced,
        isLoadMore: true,
      })
    );
  }, [
    dispatch,
    currentPage,
    searchDebounced,
    referralsLoading,
    hasMoreReferrals,
  ]);

  // Handle search changes
  useEffect(() => {
    if (!isInitialLoad.current) {
      dispatch(resetReferrals());
      dispatch(setSearchQuery(searchDebounced));
      dispatch(
        fetchReferralsItems({
          page: 1,
          limit: 20,
          search: searchDebounced,
          isLoadMore: false,
        })
      );
    }
  }, [searchDebounced, dispatch]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreReferrals && !referralsLoading) {
          loadMoreData();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "20px",
      }
    );

    observerRef.current = observer;

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreData, hasMoreReferrals, referralsLoading]);

  const handleClick = (item) => {
    navigate(`/adminreferralsone/${item._id}`);
  };

  const handleExport = async () => {
    try {
      // Fetch all data for export (without pagination)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/referrals`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-user-role": "admin",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const { data: exportData } = await response.json();

      if (!exportData || exportData.length === 0) {
        alert("No data to export");
        return;
      }

      const headers = [
        "referral_id",
        "company_website_url",
        "industry_sector",
        "location",
        "created_at",
        "member_name",
        "referral_member_status",
        "vendor_name",
        "referral_status",
        "notes",
        "dispute",
        "has_budget",
        "is_decision_maker",
        "urgent_problem",
        "timeframe",
        "relevant_context_of_referral",
        "expected_close_date",
        "reward",
      ];

      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const str = value.toString();
        if (str.includes(",") || str.includes("\n") || str.includes('"')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvRows = [];
      csvRows.push(headers.join(","));

      // Process export data similar to processedData
      const processedExportData = exportData.map((referral) => {
        const memberData = referral.member_id;
        const vendorData = referral.vendor_id;
        const dispute = referral.dispute;

        return {
          ...referral,
          member: memberData || null,
          vendor: vendorData || null,
          dispute: dispute || null,
        };
      });

      processedExportData.forEach((val) => {
        const row = [
          "R" + val?._id.slice(-7),
          val?.company_website_url,
          val?.industry_sector,
          val?.location ? val?.location : "",
          val.created_at ? formattedDate(val.created_at) : "",
          val.member == null
            ? ""
            : val.member.first_name + " " + val.member.last_name,
          val.referral_member_status ? val.referral_member_status : "",
          val.vendor_id == null ? "" : val.vendor_id.company_name,
          val.referral_status ? val.referral_status : "",
          val?.notes,
          val?.dispute ? "Yes" : "No",
          val?.has_budget,
          val?.is_decision_maker,
          val?.urgent_problem,
          val?.timeframe,
          val?.relevant_context_of_referral,
          val?.expected_close_date,
          val?.reward
            ? val.reward.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "",
        ].map(escapeCSV);

        csvRows.push(row.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "Referrals.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    }
  };
  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchDebounced(search);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Process referrals data with related entities
  useEffect(() => {
    if (referrals.length > 0) {
      const enrichedData = referrals.map((referral) => {
        const memberData = referral.member_id;

        const dispute = referral.dispute;

        return {
          _id: referral._id,
          company_website_url: referral?.company_website_url,
          industry_sector: referral?.industry_sector,
          location: referral?.location,
          referral_id: "R" + referral._id.slice(-7),
          created_at: referral.created_at
            ? formattedDate(referral.created_at)
            : "",
          member_name:
            memberData == null
              ? ""
              : memberData.first_name + " " + memberData.last_name,
          member: memberData || null,
          referral_member_status: referral.referral_member_status || "",
          vendor_name:
            referral.vendor_id == null ? "" : referral.vendor_id.company_name,
          vendor: referral.vendor_id || null,
          referral_status: referral.referral_status || "",
          notes: referral?.notes,
          dispute: dispute || null,
          has_budget: referral?.has_budget,
          is_decision_maker: referral?.is_decision_maker,
          urgent_problem: referral?.urgent_problem,
          timeframe: referral?.timeframe,
          relevant_context_of_referral: referral?.relevant_context_of_referral,
          expected_close_date: referral?.expected_close_date,
          is_urgent: referral?.is_urgent,
          reward: referral?.reward
            ? referral?.reward.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "",
        };
      });

      setProcessedData(enrichedData);
    } else {
      setProcessedData([]);
    }
  }, [referrals]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([dispatch(fetchReferralsItems())]);

        dispatch(
          fetchReferralsItems({
            page: 1,
            limit: 20,
            isLoadMore: false,
          })
        );
        const tableColumns = [
          { key: "referral_id", width: "15%" },
          { key: "created_at", width: "13%" },
          { key: "member_name", width: "14%" },
          { key: "referral_member_status", width: "14%" },
          { key: "vendor_name", width: "12%" },
          { key: "referral_status", width: "18%" },
          { key: "action", width: "9%" },
        ];

        const tableHeaders = [
          { label: "Referral ID", width: "15%" },
          { label: "Date", width: "13%" },
          { label: "Member", width: "14%" },
          { label: "Member Status", width: "14%" },
          { label: "Vendor", width: "12%" },
          { label: "Vendor Status", width: "18%" },
          { label: "Action", width: "9%" },
        ];

        setTableColumn(tableColumns);
        setTableHeader(tableHeaders);
        isInitialLoad.current = false;
      } catch (e) {
        console.log(e, "error");
      }
    };

    initializeData();
  }, [dispatch]);

  return (
    <RootWrapperAdminreferralsone>
      <Navbar />
      <Dashboard>
        <Wrapper>
          <Left_0001>
            <PRTNR.SidebarNav />
          </Left_0001>
          <Right>
            <DashboardHeader>
              <Frame1462>
                <WelcomeJames>Referrals</WelcomeJames>
              </Frame1462>
              <Frame1461>
                <AIMEButton
                  size="small"
                  onClick={handleExport}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    background: "rgb(33, 37, 67)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Export
                  </span>
                </AIMEButton>
              </Frame1461>
            </DashboardHeader>
            <RecentReferrals>
              <TableTitle>
                <Frame1431>
                  <Frame1431_0001>
                    <Title>Submitted Referrals</Title>
                  </Frame1431_0001>
                </Frame1431>
                <Frame1432>
                  <GFieldsStacked>
                    <GFieldsStacked_0001>
                      <PRTNR.Input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </GFieldsStacked_0001>
                  </GFieldsStacked>
                </Frame1432>
              </TableTitle>

              <PRTNR.TableComponent
                data={processedData}
                tableColumn={tableColumn}
                tableHeader={tableHeader}
                viewClick={handleClick}
              />

              {/* Loading indicator */}
              {referralsLoading && (
                <LoadingContainer>
                  <LoadingText>Loading more referrals...</LoadingText>
                </LoadingContainer>
              )}

              {/* Sentinel element for intersection observer */}
              {hasMoreReferrals && !referralsLoading && (
                <div ref={sentinelRef} style={{ height: "20px" }} />
              )}

              {/* No results message */}
              {processedData.length === 0 && !referralsLoading && (
                <NoResultsMessage>
                  <NoResultsText>No referrals found</NoResultsText>
                </NoResultsMessage>
              )}
            </RecentReferrals>
          </Right>
        </Wrapper>
      </Dashboard>
      <Footer>
        <Section>
          <Section_0001>
            <Wrapper_0001>
              <Frame38>
                <_2025PaartnerLimitedAllRightsReserved>
                  © 2025 Paartner Limited. All Rights Reserved
                </_2025PaartnerLimitedAllRightsReserved>
              </Frame38>
            </Wrapper_0001>
          </Section_0001>
        </Section>
      </Footer>
    </RootWrapperAdminreferralsone>
  );
}
// Add new styled components for loading states
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const LoadingText = styled.span`
  color: rgb(100, 116, 139);
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
`;

const EndMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border-top: 1px solid rgb(225, 228, 234);
  margin-top: 16px;
`;

const EndText = styled.span`
  color: rgb(100, 116, 139);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  text-align: center;
`;

const NoResultsMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`;

const NoResultsText = styled.span`
  color: rgb(100, 116, 139);
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  text-align: center;
`;

// Keep all your existing styled components from the original file
const RootWrapperAdminreferralsone = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  width: 1200px;
  box-sizing: border-box;
  padding: 32px 0px;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 298px;
  box-sizing: border-box;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 1078px;
  box-sizing: border-box;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 878px;
  box-sizing: border-box;
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: center;
`;

const RecentReferrals = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const TableTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1431 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
`;

const Frame1431_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Title = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 20px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 600px;
  box-sizing: border-box;
`;

const GFieldsStacked = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const GFieldsStacked_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 4px;
  box-sizing: border-box;
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 112px;
  box-sizing: border-box;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;
